import React, { useEffect, useState, useRef } from "react";
import { Link, useHistory } from "react-router-dom";
import {
  Box,
  Button,
  Container,
  Grid,
  Typography,
  useTheme,
  useMediaQuery,
  Fade,
  Slide,
  Grow,
  Zoom,
} from "@mui/material";
import {
  Pets,
  Favorite,
  PlayArrow,
  Home,
  ShoppingCart,
  School,
  Search,
  Chat,
  Campaign,
  Star,
  TrendingUp,
  Groups,
  CheckCircle,
} from "@mui/icons-material";
import Logo from "../../images/logo-petlover.png";
import MarketplacePet from "../../images/marketplace-pet.jpg";
import LostFoundPet from "../../images/lost-found-pet.jpg";
import AdoptionCenterPet from "../../images/adoption-center-pet.jpg";
import WhyPetImageOne from "../../images/pet-images-01.jpg";
import WhyPetImageTwo from "../../images/pet-images-02.jpg";
import WhyPetImageThree from "../../images/pet-images-03.jpg";
import "./LandingPage.css";

const LandingPage: React.FC = () => {
  const history = useHistory();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [isVisible, setIsVisible] = useState(false);
  const [heroAnimationsReady, setHeroAnimationsReady] = useState(false);
  const [scrollY, setScrollY] = useState(0);
  const [visibleCards, setVisibleCards] = useState<Set<number>>(new Set());
  const [visibleWhyCards, setVisibleWhyCards] = useState<Set<number>>(new Set());
  const [isValueSectionVisible, setIsValueSectionVisible] = useState(false);
  const [isCTASectionVisible, setIsCTASectionVisible] = useState(false);
  const [mounted, setMounted] = useState(false);
  const heroRef = useRef<HTMLDivElement>(null);
  const valueSectionRef = useRef<HTMLDivElement>(null);
  const ctaSectionRef = useRef<HTMLDivElement>(null);
  const cardRefs = useRef<(HTMLDivElement | null)[]>([]);
  const whyCardRefs = useRef<(HTMLDivElement | null)[]>([]);
  
  // Initialize refs arrays
  useEffect(() => {
    cardRefs.current = cardRefs.current.slice(0, features.length);
    whyCardRefs.current = whyCardRefs.current.slice(0, 3);
  }, []);

  useEffect(() => {
    setMounted(true);
    
    // Trigger hero animations after component mounts - start false then animate to true
    const heroTimer = setTimeout(() => {
      setHeroAnimationsReady(true);
      setIsVisible(true);
    }, 100);
    
    // Trigger animation for Why section cards
    const whyTimer = setTimeout(() => {
      setVisibleWhyCards(new Set([0, 1, 2]));
    }, 200);
    
    const handleScroll = () => {
      setScrollY(window.scrollY);
      
      // Check which feature cards are visible - reduced threshold for earlier animation
      const newVisibleCards = new Set<number>();
      cardRefs.current.forEach((ref, index) => {
        if (ref) {
          const rect = ref.getBoundingClientRect();
          // Cards animate when they're 200px away from entering viewport
          const isVisible = rect.top < window.innerHeight + 200 && rect.bottom > -200;
          if (isVisible) {
            newVisibleCards.add(index);
          }
        }
      });
      setVisibleCards(newVisibleCards);
      
      // Check which Why section cards are visible
      const newVisibleWhyCards = new Set<number>();
      whyCardRefs.current.forEach((ref, index) => {
        if (ref) {
          const rect = ref.getBoundingClientRect();
          const isVisible = rect.top < window.innerHeight * 0.8 && rect.bottom > 0;
          if (isVisible) {
            newVisibleWhyCards.add(index);
          }
        }
      });
      setVisibleWhyCards(newVisibleWhyCards);
    };
    
    // Check scroll position after refs are ready - this will animate all visible cards including first 3
    const scrollTimer = setTimeout(() => {
      handleScroll();
    }, 200);
    
    // Check if value section is visible
    const checkValueSection = () => {
      if (valueSectionRef.current) {
        const rect = valueSectionRef.current.getBoundingClientRect();
        const isVisible = rect.top < window.innerHeight * 0.8 && rect.bottom > 0;
        setIsValueSectionVisible(isVisible);
      }
    };
    
    // Check if CTA section is visible
    const checkCTASection = () => {
      if (ctaSectionRef.current) {
        const rect = ctaSectionRef.current.getBoundingClientRect();
        const isVisible = rect.top < window.innerHeight * 0.8 && rect.bottom > 0;
        setIsCTASectionVisible(isVisible);
      }
    };
    
    const valueSectionTimer = setTimeout(() => {
      checkValueSection();
    }, 300);
    
    const ctaSectionTimer = setTimeout(() => {
      checkCTASection();
    }, 300);
    
    const handleScrollWithValue = () => {
      handleScroll();
      checkValueSection();
      checkCTASection();
    };
    
    window.addEventListener("scroll", handleScrollWithValue);
    return () => {
      clearTimeout(heroTimer);
      clearTimeout(whyTimer);
      clearTimeout(scrollTimer);
      clearTimeout(valueSectionTimer);
      clearTimeout(ctaSectionTimer);
      window.removeEventListener("scroll", handleScrollWithValue);
    };
  }, []);

  const handleGetStarted = () => {
    const isLoggedIn = Boolean(localStorage.getItem("userData"));
    if (isLoggedIn) {
      history.push("/home");
    } else {
      history.push("/login");
    }
  };

  const features = [
    {
      icon: <Star sx={{ fontSize: 48 }} />,
      title: "PET OF THE DAY",
      description: "Celebrate special pets every day. Share, vote, and inspire the community with your furry friends.",
      actions: ["📸 Share", "❤️ Vote", "🐶🐱 Inspire"],
      color: "#01B4BC",
      image: "https://images.unsplash.com/photo-1552053831-71594a27632d?w=400&h=300&fit=crop",
    },
    {
      icon: <PlayArrow sx={{ fontSize: 48 }} />,
      title: "PET PLAY DATES",
      description: "Connect with local pet parents. Organize safe playdates and build lasting friendships for your pets.",
      tagline: "Because pets need friends too.",
      color: "#01B4BC",
      image: "https://images.unsplash.com/photo-1551717743-49959800b1f6?w=400&h=300&fit=crop",
    },
    {
      icon: <Home sx={{ fontSize: 48 }} />,
      title: "ADOPTION CENTER",
      description: "Find forever homes for pets in need. Connect with shelters, rescues, and loving families ready to adopt.",
      actions: ["Adopt", "Foster", "Save a life"],
      color: "#01B4BC",
      image: AdoptionCenterPet,
    },
    {
      icon: <ShoppingCart sx={{ fontSize: 48 }} />,
      title: "MARKETPLACE",
      description: "Buy and sell pet essentials with trusted pet lovers. From toys to services, find everything your pet needs.",
      tagline: "Shop smart. Trust more. Love better.",
      color: "#01B4BC",
      image: MarketplacePet,
    },
    {
      icon: <School sx={{ fontSize: 48 }} />,
      title: "PET TRICKS & TRAINING",
      description: "Learn expert training tips and fun tricks. Help your pet thrive with guidance from experienced trainers.",
      tagline: "Every pet can learn. Every trick counts.",
      color: "#01B4BC",
      image: "https://images.unsplash.com/photo-1587300003388-59208cc962cb?w=400&h=300&fit=crop",
    },
    {
      icon: <Search sx={{ fontSize: 48 }} />,
      title: "LOST & FOUND",
      description: "Reunite lost pets with their families. Get instant community alerts and support when pets go missing.",
      tagline: "Hope starts here.",
      color: "#01B4BC",
      image: LostFoundPet,
    },
    {
      icon: <Chat sx={{ fontSize: 48 }} />,
      title: "PET CHAT ROOMS",
      description: "Join conversations with real pet people. Share stories, get advice, and connect with fellow pet lovers.",
      color: "#01B4BC",
      image: "https://images.unsplash.com/photo-1574158622682-e40e69881006?w=400&h=300&fit=crop",
    },
    {
      icon: <Campaign sx={{ fontSize: 48 }} />,
      title: "AD CENTER",
      description: "A modern, affordable, and innovative platform for companies of all sizes. Our ad center is designed for all business sectors.",
      highlight: true,
      color: "#FFD93D",
      image: "https://images.unsplash.com/photo-1517849845537-4d257902454a?w=400&h=300&fit=crop",
    },
  ];

  // Hero section fade out/in on scroll - reappears faster when scrolling back up
  // Adjusted thresholds so it fades out slower and reappears sooner
  const heroOpacity = Math.max(0, Math.min(1, 1 - scrollY / 600));
  const heroScale = Math.max(0.88, Math.min(1, 1 - scrollY / 1500));

  return (
    <Box sx={{ overflow: "hidden", bgcolor: "#f0f9fa" }}>
      {/* Hero Section */}
      <Box
        ref={heroRef}
        sx={{
          minHeight: "100vh",
          // Solid app primary teal
          background: "#01B4BC",
          position: "relative",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          overflow: "hidden",
          opacity: heroOpacity,
          transform: `scale(${heroScale})`,
          transition: "opacity 0.3s ease, transform 0.3s ease",
        }}
      >
        {/* Animated Background Elements */}
        <Box
          className="floating-circle circle-1"
          sx={{
            position: "absolute",
            width: "600px",
            height: "600px",
            borderRadius: "50%",
            background: "rgba(255, 255, 255, 0.1)",
            top: "-300px",
            right: "-300px",
          }}
        />
        <Box
          className="floating-circle circle-2"
          sx={{
            position: "absolute",
            width: "500px",
            height: "500px",
            borderRadius: "50%",
            background: "rgba(255, 255, 255, 0.08)",
            bottom: "-250px",
            left: "-250px",
          }}
        />

        <Container maxWidth="lg">
          <Fade in={heroAnimationsReady && isVisible} timeout={800}>
            <Box sx={{ textAlign: "center", color: "white", position: "relative", zIndex: 1 }}>
              <Zoom in={heroAnimationsReady && isVisible} timeout={1000} style={{ transitionDelay: "200ms" }}>
                <Box>
                  <img
                    src={Logo}
                    alt="PetLoversChat"
                    style={{
                      maxWidth: isMobile ? "200px" : "300px",
                      marginBottom: "30px",
                      filter: "drop-shadow(0 10px 30px rgba(0,0,0,0.3))",
                    }}
                  />
                </Box>
              </Zoom>

              <Slide direction="down" in={heroAnimationsReady && isVisible} timeout={800} style={{ transitionDelay: "400ms" }}>
                <Box>
                  <Typography
                    variant="h1"
                    sx={{
                      fontSize: isMobile ? "2.5rem" : "4rem",
                      fontWeight: 800,
                      mb: 2,
                      textShadow: "2px 2px 4px rgba(0,0,0,0.2)",
                      letterSpacing: "-0.02em",
                    }}
                  >
                    PETLOVERSCHAT
                  </Typography>
                </Box>
              </Slide>

              <Slide direction="down" in={heroAnimationsReady && isVisible} timeout={800} style={{ transitionDelay: "600ms" }}>
                <Box>
                  <Typography
                    variant="h4"
                    sx={{
                      fontSize: isMobile ? "1.2rem" : "1.8rem",
                      fontWeight: 300,
                      mb: 3,
                      opacity: 0.95,
                    }}
                  >
                    The Ultimate Social Hub for Pet Lovers
                  </Typography>
                </Box>
              </Slide>

              <Slide direction="up" in={heroAnimationsReady && isVisible} timeout={800} style={{ transitionDelay: "800ms" }}>
                <Box>
                  <Typography
                    variant="h6"
                    sx={{
                      fontSize: isMobile ? "1rem" : "1.3rem",
                      mb: 4,
                      opacity: 0.9,
                      maxWidth: "800px",
                      mx: "auto",
                      lineHeight: 1.6,
                    }}
                  >
                    Connect. Discover. Adopt. Play. Shop. Learn.
                    <br />
                    Everything pets—and pet parents—need, all in one place.
                  </Typography>
                </Box>
              </Slide>

              <Fade in={heroAnimationsReady && isVisible} timeout={800} style={{ transitionDelay: "1000ms" }}>
                <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", mt: 4 }}>
                  <Button
                    variant="contained"
                    size="large"
                    onClick={handleGetStarted}
                    sx={{
                      bgcolor: "white",
                      // primary teal from app theme
                      color: "#01B4BC",
                      px: 4,
                      py: 1.5,
                      fontSize: "1.1rem",
                      fontWeight: 600,
                      borderRadius: "50px",
                      textTransform: "none",
                      boxShadow: "0 10px 30px rgba(0,0,0,0.3)",
                      "&:hover": {
                        bgcolor: "#f5f5f5",
                        transform: "translateY(-2px)",
                        boxShadow: "0 15px 40px rgba(0,0,0,0.4)",
                      },
                      transition: "all 0.3s ease",
                    }}
                  >
                    Get Started
                  </Button>
                  <Box sx={{ mt: 10, display: "flex", gap: 2, justifyContent: "center", flexWrap: "wrap" }}>
                    <Button
                      component="a"
                      href="https://apps.apple.com/in/app/petloverschat/id1603400503"
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{
                        bgcolor: "transparent",
                        color: "#000",
                        px: 0,
                        py: 0,
                        textTransform: "none",
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        "&:hover": {
                          bgcolor: "transparent",
                          transform: "translateY(-2px)",
                        },
                        transition: "all 0.3s ease",
                      }}
                    >
                      <Box
                        component="img"
                        src="https://developer.apple.com/assets/elements/badges/download-on-the-app-store.svg"
                        alt="Download on the App Store"
                        sx={{
                          height: isMobile ? "52px" : "65px",
                          width: "auto",
                        }}
                      />
                    </Button>
                    <Button
                      component="a"
                      href="https://play.google.com/store/apps/details?id=com.petlovers.app"
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{
                        bgcolor: "transparent",
                        color: "#000",
                        px: 0,
                        py: 0,
                        textTransform: "none",
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        "&:hover": {
                          bgcolor: "transparent",
                          transform: "translateY(-2px)",
                        },
                        transition: "all 0.3s ease",
                      }}
                    >
                      <Box
                        component="img"
                        src="https://play.google.com/intl/en_us/badges/static/images/badges/en_badge_web_generic.png"
                        alt="Get it on Google Play"
                        sx={{
                          height: isMobile ? "74px" : "94px",
                          width: "auto",
                        }}
                      />
                    </Button>
                  </Box>
                </Box>
              </Fade>
            </Box>
          </Fade>
        </Container>

        {/* Scroll Indicator */}
        <Fade in={heroAnimationsReady && isVisible} timeout={1500}>
          <Box
            className="scroll-indicator"
            sx={{
              position: "absolute",
              bottom: "30px",
              left: "50%",
              transform: "translateX(-50%)",
              color: "white",
              fontSize: "24px",
            }}
          >
            ↓
          </Box>
        </Fade>
      </Box>

      {/* Features Section */}
      <Box sx={{ bgcolor: "#f0f9fa", py: 10 }}>
        <Container maxWidth="lg">
          <Fade in={true} timeout={1000}>
            <Typography
              variant="h2"
              sx={{
                textAlign: "center",
                mb: 8,
                fontWeight: 700,
                fontSize: isMobile ? "2rem" : "3rem",
                color: "#000000",
              }}
            >
              Everything You Need for Your Pet
            </Typography>
          </Fade>

        <Grid 
          container 
          spacing={8} 
          alignItems="stretch"
          sx={{ 
            mb: 4,
            "& > .MuiGrid-item": {
              paddingTop: "32px !important",
              paddingBottom: "32px !important",
            }
          }}
        >
          {features.map((feature, index) => (
            <Grid item xs={12} md={6} lg={4} key={index} sx={{ mb: 2 }}>
              <Box
                ref={(el: HTMLDivElement | null) => {
                  if (el) {
                    cardRefs.current[index] = el;
                  }
                }}
                className="feature-card"
                sx={{
                  pt: 4,
                  px: 4,
                  pb: 1,
                  mb: 3,
                  borderRadius: "20px",
                  background: feature.highlight 
                    ? "linear-gradient(135deg, #FFD93D 0%, #FFA500 100%)" 
                    : `linear-gradient(135deg, ${feature.color}15 0%, #01B4BC20 100%)`,
                  bgcolor: feature.highlight ? "transparent" : "transparent",
                  boxShadow: feature.highlight
                    ? "0 20px 60px rgba(255, 217, 61, 0.4)"
                    : "0 10px 40px rgba(1, 180, 188, 0.2)",
                  minHeight: "100%",
                  border: feature.highlight ? "3px solid #FFD93D" : `2px solid ${feature.color}40`,
                  position: "relative",
                  overflow: "hidden",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "flex-start",
                  transition: "opacity 1s cubic-bezier(0.25, 0.46, 0.45, 0.94), transform 1s cubic-bezier(0.25, 0.46, 0.45, 0.94), background 0.3s ease, border 0.3s ease, boxShadow 0.3s ease",
                  transform: visibleCards.has(index) ? "translateY(0) scale(1)" : "translateY(50px) scale(0.96)",
                  opacity: visibleCards.has(index) ? 1 : 0,
                  transitionDelay: visibleCards.has(index) ? `${index * 100}ms` : "0ms",
                  willChange: "opacity, transform",
                    "&:hover": {
                      boxShadow: feature.highlight
                        ? "0 25px 70px rgba(255, 217, 61, 0.5)"
                        : "0 15px 50px rgba(1, 180, 188, 0.3)",
                      transform: "translateY(-10px) scale(1.02)",
                      transition: "transform 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94), background 0.3s ease, border 0.3s ease, boxShadow 0.3s ease, color 0.3s ease",
                      borderColor: feature.highlight ? "#FFD93D" : "#01B4BC",
                      background: feature.highlight 
                        ? "linear-gradient(135deg, #FFD93D 0%, #FFA500 100%)" 
                        : "#01B4BC",
                      "& .feature-icon": {
                        color: "white",
                        "& svg": {
                          filter: "drop-shadow(2px 2px 4px rgba(0, 0, 0, 0.8)) drop-shadow(0 0 8px rgba(0, 0, 0, 0.6))",
                        },
                      },
                      "& .feature-title": {
                        color: "white",
                        textShadow: "2px 2px 4px rgba(0, 0, 0, 0.8), 0 0 8px rgba(0, 0, 0, 0.6)",
                      },
                      "& .feature-description": {
                        color: "white",
                        textShadow: "1px 1px 3px rgba(0, 0, 0, 0.9), 0 0 6px rgba(0, 0, 0, 0.7)",
                      },
                      "& .feature-tagline": {
                        color: "white",
                        textShadow: "1px 1px 3px rgba(0, 0, 0, 0.9), 0 0 6px rgba(0, 0, 0, 0.7)",
                      },
                    },
                  }}
                >
                  {feature.highlight && (
                    <Box
                      sx={{
                        position: "absolute",
                        top: 10,
                        right: 10,
                        bgcolor: "#FF6B6B",
                        color: "white",
                        px: 2,
                        py: 0.5,
                        borderRadius: "20px",
                        fontSize: "0.75rem",
                        fontWeight: 700,
                        zIndex: 2,
                      }}
                    >
                      HIGHLIGHT
                    </Box>
                  )}

                  {/* Animal Image */}
                  {feature.image && (
                    <Box
                      sx={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        height: "180px",
                        overflow: "hidden",
                        borderRadius: "20px 20px 0 0",
                        opacity: 0.75,
                        zIndex: 0,
                      }}
                    >
                      <Box
                        component="img"
                        src={feature.image}
                        alt={feature.title}
                        sx={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                          filter: "brightness(1.2) contrast(1.2)",
                        }}
                      />
                      {/* Gradient fade overlay at bottom */}
                      <Box
                        sx={{
                          position: "absolute",
                          bottom: 0,
                          left: 0,
                          right: 0,
                          height: "60px",
                          background: "linear-gradient(to bottom, transparent 0%, rgba(240, 249, 250, 0.3) 50%, rgba(240, 249, 250, 0.8) 100%)",
                          pointerEvents: "none",
                        }}
                      />
                    </Box>
                  )}

                  <Box
                    className="feature-icon"
                    sx={{
                      color: feature.color,
                      mb: 2,
                      display: "flex",
                      justifyContent: "center",
                      transition: "color 0.3s ease",
                      position: "relative",
                      zIndex: 1,
                      "& svg": {
                        filter: "drop-shadow(2px 2px 4px rgba(255, 255, 255, 0.8)) drop-shadow(0 0 8px rgba(255, 255, 255, 0.6))",
                      },
                    }}
                  >
                    {feature.icon}
                  </Box>

                  <Typography
                    className="feature-title"
                    variant="h5"
                    sx={{
                      fontWeight: 700,
                      mb: 2,
                      textAlign: "center",
                      color: feature.highlight ? "#333" : "#333",
                      transition: "color 0.3s ease",
                      position: "relative",
                      zIndex: 1,
                      textShadow: "2px 2px 4px rgba(255, 255, 255, 0.8), 0 0 8px rgba(255, 255, 255, 0.6)",
                    }}
                  >
                    {feature.title}
                  </Typography>

                  <Typography
                    className="feature-description"
                    variant="body1"
                    sx={{
                      mb: 2,
                      color: "#666",
                      lineHeight: 1.8,
                      textAlign: "center",
                      transition: "color 0.3s ease",
                      position: "relative",
                      zIndex: 1,
                      textShadow: "1px 1px 3px rgba(255, 255, 255, 0.9), 0 0 6px rgba(255, 255, 255, 0.7)",
                    }}
                  >
                    {feature.description}
                  </Typography>

                  {feature.actions && (
                    <Box sx={{ display: "flex", gap: 1, justifyContent: "center", flexWrap: "wrap", mt: 2, position: "relative", zIndex: 1 }}>
                      {feature.actions.map((action, i) => (
                        <Typography
                          key={i}
                          variant="caption"
                          sx={{
                            bgcolor: feature.color,
                            color: "white",
                            px: 2,
                            py: 0.5,
                            borderRadius: "15px",
                            fontWeight: 600,
                          }}
                        >
                          {action}
                        </Typography>
                      ))}
                    </Box>
                  )}

                  {feature.tagline && (
                    <Typography
                      className="feature-tagline"
                      variant="body2"
                      sx={{
                        mt: 2,
                        mb: 0,
                        fontStyle: "italic",
                        color: feature.color,
                        fontWeight: 600,
                        textAlign: "center",
                        transition: "color 0.3s ease",
                        position: "relative",
                        zIndex: 1,
                      }}
                    >
                      {feature.tagline}
                    </Typography>
                  )}
                  {!feature.tagline && !feature.actions && (
                    <Box sx={{ mb: 0 }} />
                  )}
                </Box>
            </Grid>
          ))}
        </Grid>
        </Container>
      </Box>

      {/* Why PetLoversChat Section */}
      <Box
        sx={{
          background: "linear-gradient(135deg, #01B4BC 0%, #0195a2 100%)",
          pt: 4,
          pb: 4,
          position: "relative",
          overflow: "hidden",
        }}
      >
        <Fade in={true} timeout={1000}>
          <Container maxWidth="lg">
            <Typography
              variant="h2"
              sx={{
                textAlign: "center",
                mt: 1,
                mb: 8,
                fontWeight: 700,
                fontSize: isMobile ? "2rem" : "3rem",
                color: "white",
              }}
            >
              WHY PETLOVERSCHAT?
            </Typography>

            <Grid 
              container 
              spacing={8} 
              alignItems="stretch"
              sx={{ 
                mb: 4,
                "& > .MuiGrid-item": {
                  paddingTop: "32px !important",
                  paddingBottom: "32px !important",
                }
              }}
            >
              {[
                {
                  title: "Because pet love is better when it's shared.",
                  icon: <Favorite sx={{ fontSize: 60, color: "#FF6B6B" }} />,
                  image: WhyPetImageOne,
                },
                {
                  title: "Because answers, connection, and community should be easy.",
                  icon: <Groups sx={{ fontSize: 60, color: "#4ECDC4" }} />,
                  image: WhyPetImageTwo,
                },
                {
                  title: "Because pets are family.",
                  icon: <Pets sx={{ fontSize: 60, color: "#95E1D3" }} />,
                  image: WhyPetImageThree,
                },
              ].map((item, index) => (
                <Grid item xs={12} md={4} key={index} sx={{ mb: 2 }}>
                  <Box
                    ref={(el: HTMLDivElement | null) => {
                      if (el) {
                        whyCardRefs.current[index] = el;
                      }
                    }}
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      textAlign: "center",
                      pt: 4,
                      px: 4,
                      pb: 0.5,
                      mb: 3,
                      background: "linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(255, 255, 255, 0.9) 100%)",
                      bgcolor: "transparent",
                      borderRadius: "20px",
                      boxShadow: "0 10px 40px rgba(0,0,0,0.15)",
                      minHeight: "100%",
                      position: "relative",
                      overflow: "hidden",
                      transition: "opacity 0.8s cubic-bezier(0.34, 1.56, 0.64, 1), transform 0.8s cubic-bezier(0.34, 1.56, 0.64, 1)",
                      transform: visibleWhyCards.has(index) ? "translateX(0) scale(1)" : index % 2 === 0 ? "translateX(-50px) scale(0.95)" : "translateX(50px) scale(0.95)",
                      opacity: visibleWhyCards.has(index) ? 1 : 0,
                      transitionDelay: `${index * 200}ms`,
                      willChange: "opacity, transform",
                      "&:hover": {
                        boxShadow: "0 15px 50px rgba(0,0,0,0.2)",
                        transform: "translateY(-5px) scale(1.02)",
                      },
                    }}
                  >
                    {item.image && (
                      <Box
                        sx={{
                          position: "absolute",
                          top: 0,
                          left: 0,
                          right: 0,
                          height: "180px",
                          overflow: "hidden",
                          borderRadius: "20px 20px 0 0",
                          opacity: 0.75,
                          zIndex: 0,
                        }}
                      >
                        <Box
                          component="img"
                          src={item.image}
                          alt={item.title}
                          sx={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                            filter: "brightness(1.2) contrast(1.2)",
                          }}
                        />
                        <Box
                          sx={{
                            position: "absolute",
                            bottom: 0,
                            left: 0,
                            right: 0,
                            height: "60px",
                            background: "linear-gradient(to bottom, transparent 0%, rgba(240, 249, 250, 0.3) 50%, rgba(240, 249, 250, 0.8) 100%)",
                            pointerEvents: "none",
                          }}
                        />
                      </Box>
                    )}
                    <Box
                      className="why-card-icon"
                      sx={{
                        mb: 2,
                        display: "flex",
                        justifyContent: "center",
                        transition: "color 0.3s ease",
                        position: "relative",
                        zIndex: 1,
                        "& svg": {
                          filter:
                            "drop-shadow(2px 2px 4px rgba(255, 255, 255, 0.8)) drop-shadow(0 0 8px rgba(255, 255, 255, 0.6))",
                        },
                      }}
                    >
                      {item.icon}
                    </Box>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 700,
                        mb: 0,
                        textAlign: "center",
                        color: "#333",
                        transition: "color 0.3s ease",
                        position: "relative",
                        zIndex: 1,
                        textShadow: "2px 2px 4px rgba(255, 255, 255, 0.8), 0 0 8px rgba(255, 255, 255, 0.6)",
                      }}
                    >
                      {item.title}
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Container>
        </Fade>
      </Box>

      {/* Value Proposition Section */}
      <Container maxWidth="lg" sx={{ py: 10 }}>
        <Box
          ref={valueSectionRef}
          sx={{
            background: "#01B4BC",
            borderRadius: "30px",
            p: 6,
            color: "white",
            textAlign: "center",
            position: "relative",
            overflow: "hidden",
            transition: "opacity 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94), transform 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94)",
            opacity: isValueSectionVisible ? 1 : 0,
            transform: isValueSectionVisible ? "translateY(0) scale(1)" : "translateY(40px) scale(0.96)",
          }}
        >
            <Box
              className="pulse-circle"
              sx={{
                position: "absolute",
                width: "400px",
                height: "400px",
                borderRadius: "50%",
                background: "rgba(255, 255, 255, 0.1)",
                top: "-200px",
                right: "-200px",
              }}
            />

            <Slide direction="up" in={isValueSectionVisible} timeout={700}>
              <Typography
                variant="h3"
                sx={{
                  fontWeight: 700,
                  mb: 3,
                  fontSize: isMobile ? "1.8rem" : "2.5rem",
                  position: "relative",
                  zIndex: 1,
                }}
              >
                Pet parenting doesn't come with a manual.
              </Typography>
            </Slide>
            <Slide direction="up" in={isValueSectionVisible} timeout={800} style={{ transitionDelay: "150ms" }}>
              <Typography
                variant="h5"
                sx={{
                  mb: 4,
                  opacity: 0.95,
                  fontSize: isMobile ? "1.2rem" : "1.5rem",
                  position: "relative",
                  zIndex: 1,
                }}
              >
                PETLOVERSCHAT gives you clarity, confidence, and support—when you need it most.
              </Typography>
            </Slide>

            <Box sx={{ display: "flex", gap: 4, justifyContent: "center", flexWrap: "wrap", mt: 5, position: "relative", zIndex: 1 }}>
              {[
                { icon: CheckCircle, text: "Reliable Information" },
                { icon: TrendingUp, text: "Expert Guidance" },
                { icon: Groups, text: "Community Support" },
              ].map((item, index) => {
                const IconComponent = item.icon;
                return (
                  <Zoom in={isValueSectionVisible} timeout={600} key={index} style={{ transitionDelay: `${index * 200}ms` }}>
                    <Box sx={{ textAlign: "center" }}>
                      <Box sx={{ mb: 1, display: "flex", justifyContent: "center" }}>
                        <IconComponent sx={{ fontSize: "3rem" }} />
                      </Box>
                      <Typography variant="body1" sx={{ fontWeight: 600 }}>
                        {item.text}
                      </Typography>
                    </Box>
                  </Zoom>
                );
              })}
            </Box>
          </Box>
      </Container>

      {/* CTA Section */}
      <Box
        ref={ctaSectionRef}
        sx={{
          background: "linear-gradient(135deg, #01B4BC 0%, #0195a2 100%)",
          py: 10,
          color: "white",
          textAlign: "center",
        }}
      >
        <Container maxWidth="lg">
          <Box>
            <Slide direction="up" in={isCTASectionVisible} timeout={700}>
              <Typography
                variant="h2"
                sx={{
                  fontWeight: 800,
                  mb: 3,
                  fontSize: isMobile ? "2rem" : "3rem",
                }}
              >
                🚀 Join the fastest-growing community
              </Typography>
            </Slide>
            <Slide direction="up" in={isCTASectionVisible} timeout={800} style={{ transitionDelay: "150ms" }}>
              <Typography
                variant="h5"
                sx={{
                  mb: 5,
                  opacity: 0.9,
                  fontSize: isMobile ? "1.2rem" : "1.5rem",
                }}
              >
                Made for pets and the people who love them.
              </Typography>
            </Slide>

            <Grow in={isCTASectionVisible} timeout={900} style={{ transitionDelay: "300ms" }}>
              <Button
                variant="contained"
                size="large"
                onClick={handleGetStarted}
                sx={{
                  bgcolor: "white",
                  color: "#01B4BC",
                  px: 6,
                  py: 2,
                  fontSize: "1.3rem",
                  fontWeight: 700,
                  borderRadius: "50px",
                  textTransform: "none",
                  boxShadow: "0 10px 30px rgba(255, 255, 255, 0.4)",
                  textShadow: "2px 2px 4px rgba(255, 255, 255, 0.8), 0 0 8px rgba(255, 255, 255, 0.6)",
                  "&:hover": {
                    bgcolor: "#f5f5f5",
                    boxShadow: "0 15px 40px rgba(255, 255, 255, 0.6)",
                    transform: "scale(1.05)",
                  },
                  transition: "all 0.3s ease",
                }}
              >
                Start Celebrating Pets Today
              </Button>
            </Grow>

            <Fade in={isCTASectionVisible} timeout={1000} style={{ transitionDelay: "400ms" }}>
              <Typography
                variant="body1"
                sx={{
                  mt: 4,
                  opacity: 0.8,
                  fontStyle: "italic",
                }}
              >
                PETLOVERSCHAT — Smarter Care for Pets You Love Like Family.
              </Typography>
            </Fade>
          </Box>
        </Container>
      </Box>

      {/* Footer */}
      <Box
        sx={{
          background: "linear-gradient(135deg, #01B4BC 0%, #0195a2 100%)",
          py: 4,
          color: "white",
          textAlign: "center",
        }}
      >
        <Container maxWidth="lg">
          <Fade in={true} timeout={800}>
            <Box>
              <Box sx={{ display: "flex", justifyContent: "center", gap: 4, flexWrap: "wrap", mb: 2 }}>
                <Grow in={true} timeout={800}>
                  <Button component={Link} to="/login" sx={{ color: "white", textTransform: "none" }}>
                    Sign In
                  </Button>
                </Grow>
                <Grow in={true} timeout={900}>
                  <Button component={Link} to="/signup" sx={{ color: "white", textTransform: "none" }}>
                    Sign Up
                  </Button>
                </Grow>
                <Grow in={true} timeout={1000}>
                  <Button 
                    component="a" 
                    href="https://petloverschat.com/privacy-policy" 
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ color: "white", textTransform: "none" }}
                  >
                    Privacy Policy
                  </Button>
                </Grow>
              </Box>
              <Fade in={true} timeout={1000}>
                <Typography variant="body2" sx={{ opacity: 0.7 }}>
                  © {new Date().getFullYear()} PetLoversChat. All rights reserved.
                </Typography>
              </Fade>
            </Box>
          </Fade>
        </Container>
      </Box>
    </Box>
  );
};

export default LandingPage;
